import type { ReactNode } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import cx from 'classnames'
import Fuse from 'fuse.js'
import { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { MdAddBox, MdClose, MdSearch } from 'react-icons/md'
import { useSpring, animated } from 'react-spring'
// @ts-expect-error // it's okay
import compiler from 'shared_src/behavioral/compiler'

import behavioral from '../../../lib/behavioral'
import { isAbortErr } from '../../../lib/http'
import { useAppDispatch } from '../../../store'
import { finishLoading, startLoading } from '../../../store/modules'
import { uid } from '../../igao/utils'
import { serializeContent } from '../../RichTextEditor'
import { ErrorBanner } from '../../shared/error-banner/error-banner'
import * as Grid from '../../shared/grid'
import { Button } from '../../shared/grid'
import { CircularProgress, PortedModal } from '../../shared/NarusMaterial'

import {
  NoDataPlaceHolder,
  Note,
  Session
} from './components'
import { useBehavioralContext } from './context'

interface MutableRefObject<T> {
  current: T | null // generic type
}

interface BehavioralWidgetProps {
  patientId: string
  readOnly: boolean
}

interface DraftState {
  getting?: true
  get?: true
  setting?: true
  set?: true
  error?: Error
  publishing?: true
}

type ModalState = {
  title: string
  open: boolean
  actions: ReactNode[]
} | null

interface Node {type: string, children: Array<Record<string, unknown>>}

interface NoteT {
  'id': string
  'author'?: {
    'id': string
    'name': string
  }
  'created'?: string
  'value': {
    'plain': string
    'nodes': Node[]
  }
  diff?: boolean
  updated?: string
  'status'?: string
  isTouched?: boolean
  draft?: boolean
}

interface BehavioralData {
  notes: NoteT[]
  statuses: Array<{
    color: string
    description: string
    id: string
    sessionBackgroundColor: string
  }>
  templates: Array<{
    id: string
    title: string
    value: {
      'plain': string
      'nodes': Node[]
    }
  }>
  user: {
    id: string
    name: string
  }
}

type StateData = BehavioralData & {isTouched: boolean} & {draft: boolean}

export function BehavioralWidget ({ patientId, readOnly }: BehavioralWidgetProps): JSX.Element | null {
  let queryClient = useQueryClient()
  const dispatch = useAppDispatch()
  let [ draft, setDraft ] = useState<DraftState | null>({ getting: true })
  let [ modal, setModal ] = useState<ModalState>(null)
  let [ selectedNoteId, setSelectedNoteId ] = useState<string | null>(null)
  let [ data, setData ] = useState<StateData | null>(null)
  let [ searchQuery, setSearchQuery ] = useState('')
  let behavioralContext = useBehavioralContext()

  let selectedNoteContentRef = useRef<Node[]>(null)
  let abortControllerRef: MutableRefObject<AbortController> = useRef(null)

  useEffect(() => {
    setDraft({ getting: true })
    behavioral
      .get({
        patientId
      })
      .then(({ data }: {data: StateData}) => {
        if (!data.notes) {
          return
        }

        setDraft({ get: true })
        setData(data)
        setSelectedNoteId(data?.notes[data?.notes.length - 1]?.id ?? null)
      }).catch((error: Error) => {
        console.error(error)
        setDraft({ error })
      })

    return () => {
      setDraft(null)
      setData(null)
    }
  }, [ patientId ])

  let saveDraft = useCallback((newData: StateData) => {
    setData(newData)
    setDraft({ setting: true })

    if (abortControllerRef.current) {
      abortControllerRef.current?.abort()
    }

    let abortController = new AbortController()
    abortControllerRef.current = abortController

    let compiledData = compiler.compile({
      current: { data },
      proposed: { data: newData }
    })

    const id = 'behavioral'

    dispatch(startLoading({ description: 'Setting behavioral draft', id }))

    behavioral.set({
      patientId,
      data: compiledData.data
    }, {
      signal: abortController.signal
    })
      .then(({ data }) => {
        setData({
          ...data,
          isTouched: newData.isTouched ?? false
        })
        setDraft({ set: true })
        abortControllerRef.current = null
      })
      .catch(err => {
        if (isAbortErr(err)) {
          return
        }
        setData(data)
        setDraft({ error: err })
      })
      .finally(() => {
        dispatch(finishLoading(id))
        queryClient.invalidateQueries({ queryKey: [ 'drafts', patientId ] })
          .catch((e) => {
            console.error(e)
          })
      })
  }, [ data, dispatch, patientId, queryClient ])

  let onDiscard = useCallback((): void => {
    dispatch(startLoading({ id: 'behavioral-discard', description: 'Publishing' }))
    setDraft({ publishing: true })

    behavioral.discard({ patientId })
      .then(({ data }: {data: StateData}) => {
        if (!data?.notes?.some(n => n.id === selectedNoteId)) {
          setSelectedNoteId(data.notes[data.notes.length - 1]?.id ?? null)
        }
        setData(data)
        setDraft({ get: true })
      })
      .catch((error: Error) => {
        setDraft({ error })
      })
      .finally(() => {
        dispatch(finishLoading('behavioral-discard'))
        queryClient.invalidateQueries({ queryKey: [ 'drafts', patientId ] })
          .catch((e) => {
            console.error(e)
          })
      })
  }, [ dispatch, patientId, queryClient, selectedNoteId ])

  let openDiscardModal = useCallback(() => {
    setModal({
      title: 'Are you sure you want to cancel all changes done on this page?',
      open: true,
      actions: [
        <Button
          key={1}
          light
          primary
          onClick={() => setModal(null)}
        >
          Cancel
        </Button>,
        <Button
          primary
          key={2}
          onClick={() => {
            onDiscard()
            setModal(null)
          }}
        >
          Discard
        </Button>
      ]
    })
  }, [ onDiscard ])

  let onPublish = (): void => {
    dispatch(startLoading({ id: 'behavioral-publish', description: 'Publishing' }))
    setDraft({ publishing: true })
    behavioral.publish({
      patientId
    })
      .then(({ data }: {data: StateData}) => {
        setData(data)
        setDraft({ get: true })
      })
      .catch((err: Error) => {
        setDraft({ error: err })
      })
      .finally(() => {
        dispatch(finishLoading('behavioral-publish'))
        queryClient.invalidateQueries({ queryKey: [ 'drafts', patientId ] })
          .catch((e) => {
            console.error(e)
          })
      })
  }

  let createNewSession = (): void => {
    let newId = uid()

    let nodes: Node[] = [
      {
        type: 'paragraph',
        children: [{
          text: ''
        }]
      }
    ]

    const notes: NoteT[] = data?.notes ?? []

    if (!notes || !data) {
      return
    }

    saveDraft({
      ...data,
      notes: [
        ...(notes ?? []),
        {
          id: newId,
          status: 'default',
          value: {
            nodes,
            plain: serializeContent(nodes)
          },
          draft: true
        }
      ]
    })

    setSelectedNoteId(newId)
  }

  let onChangeStatus = (id: string): void => {
    let index = data?.notes.findIndex(note => note.id === selectedNoteId)
    let status = data?.statuses.find(status => status.id === id)

    if (typeof index !== 'number' || index === -1 || !status || !data) {
      return
    }

    const previousNote: NoteT | undefined = data?.notes[index]

    if (!previousNote) {
      return
    }

    let newData: StateData = {
      ...data,
      notes: [
        ...data?.notes.slice(0, index),
        {
          ...previousNote,
          status: status.id
        },
        ...data.notes.slice(index + 1)
      ]
    }

    saveDraft(newData)
  }

  let selectedNote = data?.notes?.find(note => note.id === selectedNoteId)

  let canSelectTemplate = (selectedNote?.value?.plain?.trim()?.length ?? 0) <= 0 && !readOnly

  let handleTemplateSelect = (id: string): void => {
    if (!canSelectTemplate) {
      return
    }

    let index = data?.notes.findIndex(note => note.id === selectedNoteId)

    if (typeof index !== 'number' || index === -1) {
      return
    }

    let template = data?.templates?.find(t => t.id === id)

    const note = data?.notes?.[index]

    if (!template || !data || !note) {
      return
    }

    let newData: StateData = {
      ...data,
      notes: [
        ...data.notes.slice(0, index),
        {
          ...note,
          value: {
            nodes: JSON.parse(JSON.stringify(template.value.nodes)),
            plain: serializeContent(template.value.nodes)
          }
        },
        ...data.notes.slice(index + 1)
      ]
    }

    saveDraft(newData)
  }

  let handleSelectedNoteDelete = useCallback(() => {
    if (!data) {
      return
    }

    let newData = {
      ...data,
      notes: data?.notes?.filter(note => note.id !== selectedNoteId) ?? []
    }

    saveDraft(newData)
    setSelectedNoteId(newData.notes[newData.notes.length - 1]?.id ?? null)
  }, [ data, saveDraft, selectedNoteId ])

  let handleSelectedNoteContentChange = useCallback((content: Node[]) => {
    if (!data) {
      return
    }

    let index = data.notes.findIndex(note => note.id === selectedNoteId)

    const id = data.notes?.[index]?.id

    if (index === -1 || !id) {
      return
    }

    let newData: StateData = {
      ...data,
      notes: [
        ...data.notes.slice(0, index),
        {
          ...data.notes[index],
          id,
          value: {
            ...data.notes[index]?.value,
            nodes: content,
            plain: serializeContent(content)
          },
          isTouched: true
        },
        ...data.notes.slice(index + 1)
      ]
    }

    saveDraft(newData)
  }, [ data, saveDraft, selectedNoteId ])

  let fuse = useMemo(() => {
    return new Fuse(data?.notes ?? [], {
      shouldSort: true,
      threshold: 0.15,
      minMatchCharLength: 2,
      findAllMatches: true,
      ignoreLocation: true,
      keys: [
        'value.plain',
        'author.name'
      ]
    })
  }, [ data?.notes ])

  let filteredNotes = useMemo(() => {
    let normalizedSearchQuery = searchQuery.trim()

    if (!normalizedSearchQuery.length) {
      return data?.notes ?? []
    }

    let result = fuse.search(normalizedSearchQuery)

    return result
      .map(({ item }) => item)
  }, [ data?.notes, fuse, searchQuery ])

  let handleSelectedNoteStartEditing = useCallback((): void => {
    const nodes = selectedNote?.value.nodes
    if (!nodes) {
      return
    }

    // @ts-expect-error // it's okay
    selectedNoteContentRef.current = nodes
  }, [ selectedNote ])

  let openDeleteModal = useCallback(() => {
    setModal({
      title: 'Are you sure you want to delete this note?',
      open: true,
      actions: [
        <Grid.Button
          key={1}
          onClick={() => setModal(null)}
          light
          primary
        >
          Cancel
        </Grid.Button>,
        <Grid.Button
          key={2}
          primary
          onClick={() => {
            handleSelectedNoteDelete()
            setModal(null)
          }}
        >
          Delete
        </Grid.Button>
      ]
    })
  }, [ handleSelectedNoteDelete ])

  // let notesExist = (data?.notes?.length ?? 0) > 0

  if (draft?.error) {
    return (
      <ErrorBanner
        color='red'
        title='Something went wrong'
        subTitle={draft?.error?.message}
      />
    )
  }

  let disablePublishVisual = !data?.draft || !!draft?.publishing || false
  let disablePublish = disablePublishVisual || !!draft?.setting

  return (
    <div
      className='tailwind'
      style={{ flex: 1 }}
    >
      <PortedModal
        open={modal?.open}
        title={modal?.title}
        actions={modal?.actions}
      />
      <div
        className={cx('bg-white rounded-2xl flex flex-row flex-1 overflow-hidden', [ behavioralContext.document && 'rounded-tr-none' ])}
        style={{ height: '75vh' }}
      >
        <div className='w-[18rem] border-r overflow-auto relative flex flex-col justify-between'>
          <Header
            createNewSession={readOnly ? undefined : createNewSession}
            query={searchQuery}
            onChange={(t) => setSearchQuery(t)}
          />
          {draft?.getting && (
            <div className='flex flex-row items-center justify-center h-full -mt-40'>
              <CircularProgress />
            </div>
          )}

          {!draft?.getting && filteredNotes.length <= 0 && !selectedNoteId && (
            <NoDataPlaceHolder
              title='There are no behavioral notes'
              className='-px-10'
            >
              <Grid.Button
                onClick={createNewSession}
                primary
                className='py-2'
              >
                New Note
              </Grid.Button>
            </NoDataPlaceHolder>
          )}

          {!draft?.getting && selectedNoteId && (
            <Sessions
              data={data}
              filteredNotes={filteredNotes}
              setSelectedNoteId={setSelectedNoteId}
              selectedNoteId={selectedNoteId}
              searchQuery={searchQuery}
            />
          )}

          <Footer
            openDiscardModal={openDiscardModal}
            disablePublish={disablePublish}
            disablePublishVisual={disablePublishVisual}
            onPublish={onPublish}
          />
        </div>
        {selectedNote && (
          <div className='flex-1'>
            <Note
              key={selectedNote.id}
              statuses={data?.statuses}
              selectedStatus={selectedNote.status}
              value={selectedNote.value.nodes ?? []}
              empty={(selectedNote?.value?.plain?.trim()?.length ?? 0) <= 0}
              isReadOnly={readOnly || !(selectedNote.draft === true || selectedNote.author?.id === data?.user.id)}
              actions={{
                onEdit: handleSelectedNoteStartEditing,
                onDelete: selectedNote.draft && !selectedNote.diff
                  ? openDeleteModal
                  : null,
                onChangeStatus,
                onChange: handleSelectedNoteContentChange
              }}
              template={{
                onChange: canSelectTemplate ? handleTemplateSelect : null,
                templates: data?.templates
              }}
            />
          </div>
        )}
      </div>
    </div>
  )
}

interface HeaderProps {
  createNewSession?: () => void
  query: string
  onChange: (t: string) => void
}

function Header ({ createNewSession, query, onChange }: HeaderProps): JSX.Element {
  const [ searchVisible, setSearchVisible ] = useState<boolean>(false)
  const ref = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (searchVisible) {
      ref?.current?.focus()
    }
  }, [ searchVisible ])

  const [ headerStyle ] = useSpring({
    opacity: searchVisible ? 0 : 1,
    transform: searchVisible
      ? 'translateX(-10rem)'
      : 'translateX(0rem)'
  }, [ searchVisible ])

  const [ input ] = useSpring({
    opacity: searchVisible ? 1 : 0,
    transform: searchVisible
      ? 'scale(1)'
      : 'scale(0.8)'
  }, [ searchVisible ])

  return (
    <div className='sticky top-0 bg-white z-10'>
      <div className='flex flex-row justify-between pl-5 pr-2 border-b border-b-gray-300 h-16 items-center relative'>
        <animated.div style={headerStyle}>
          <h2 className='text-lg text-gray-800'>Behavioral Notes</h2>
        </animated.div>
        <animated.input
          type='text'
          ref={ref}
          className={cx(
            'py-2 text-smx rounded border-emerald-400 outline-none focus:ring-0 flex-grow flex-shrink-0 absolute left-0 top-0 h-full pl-4',
            !searchVisible && 'pointer-events-none'
          )}
          placeholder='Search by note content or author'
          tabIndex={!searchVisible ? 0 : -1}
          style={{
            width: 'calc(100% - 4rem)',
            ...input
          }}
          value={query}
          onChange={e => {
            onChange(e.target.value)
          }}
          onKeyUp={event => {
            if (event.key === 'Escape') {
              setSearchVisible(false)
              onChange('')
              try {
                ref.current?.blur()
              } catch (err) {
              }
            }
          }}
        />
        <div className='flex flex-row gap-2'>
          <button
            className='text-gray-400'
            title={searchVisible ? 'Close' : 'Search'}
            onClick={() => {
              onChange('')
              setSearchVisible((prev) => !prev)
            }}
          >
            {searchVisible ? <MdClose size='1.4rem' /> : <MdSearch size='1.4rem' />}
          </button>
          {createNewSession && (
            <button
              disabled={searchVisible}
              className='text-[#0065FF]'
              onClick={createNewSession}
              title='New Note'
            >
              <MdAddBox size='1.4rem' />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

interface FooterProps {
  disablePublishVisual: boolean
  openDiscardModal: () => void
  disablePublish: boolean
  onPublish: () => void
}

function Footer ({ disablePublish, disablePublishVisual, onPublish, openDiscardModal }: FooterProps): JSX.Element {
  return (
    <div
      className='sticky bottom-0 bg-white w-full z-10 flex flex-row justify-between border-t border-t-gray-200 divide-x divide-gray-200'
    >
      <button
        className={cx('text-[12px] flex-1 text-center uppercase py-4 font-medium transition-colors', [ disablePublishVisual ? 'text-gray-300' : 'text-[#9CA3AF]' ])}
        onClick={openDiscardModal}
        disabled={disablePublish}
      >
        discard
      </button>
      <button
        className={cx('text-[12px] flex-1 text-center uppercase py-4 font-medium transition-colors', [ disablePublishVisual ? 'text-gray-300' : 'text-[#0065FF]' ])}
        onClick={onPublish}
        disabled={disablePublish}
      >
        publish
      </button>
    </div>
  )
}

interface SessionsProps {
  data: StateData | null
  filteredNotes: NoteT[]
  selectedNoteId?: string | null
  searchQuery: string
  setSelectedNoteId: (id: string) => void
}

function Sessions ({ data, filteredNotes, selectedNoteId, setSelectedNoteId, searchQuery }: SessionsProps): JSX.Element | null {
  if (filteredNotes.length <= 0 && selectedNoteId) {
    return (
      <NoDataPlaceHolder title='No results match the search criteria' />
    )
  }

  let reversedNotes = [ ...filteredNotes ].reverse()

  return (
    <>
      {reversedNotes.length > 0 && (
        <div className='flex flex-col divide-y divide-gray-200 flex-1'>
          {reversedNotes.map(note => {
            let status = data?.statuses?.find(status => status.id === note.status)

            return (
              <Session
                key={note.id}
                isActive={note.id === selectedNoteId}
                status={status}
                note={note}
                query={searchQuery}
                onClick={() => {
                  setSelectedNoteId(note.id)
                }}
              />
            )
          })}
        </div>
      )}
    </>
  )
}
